<script setup>
import { computed } from "vue";

/* Hand-built stroke icon set ported from the BuzzTracker design bundle
   (bt-charts.jsx). Kept self-contained so the mobile app matches the
   prototype exactly rather than swapping in @mdi glyphs. */
const ICONS = {
  dashboard: "M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z",
  reviews: "M21 11.5a8.4 8.4 0 0 1-12 7.6L3 21l1.9-6A8.5 8.5 0 1 1 21 11.5z",
  topics: "M3 7l4-4h6l8 8-10 10-8-8zM7.5 7.5h.01",
  entities:
    "M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18",
  // Crossed swords (mdiSwordCross) — matches the desktop sidebar icon. A closed
  // shape so it reads correctly both as a stroke outline (resting) and filled (active).
  competitors:
    "M6.2 2.44L18.1 14.34L20.22 12.22L21.63 13.63L19.16 16.1L22.34 19.28L21.63 20L20.93 20.69L17.74 17.5L15.27 19.97L13.86 18.56L15.98 16.44L4.08 4.56V2.44H6.2M15.74 7.05L18.56 4.22V2.44H16.43L13.61 5.26L15.74 7.05M8.5 14.66L6.5 12.66L4.5 14.66L7.5 17.66L8.5 16.66L9.5 17.66L11.5 15.66L9.5 13.66L8.5 14.66Z",
  social: "M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16M5 19a1.5 1.5 0 1 0 .01 0",
  analytics: "M3 3v18h18M7 14l3-4 3 3 4-7",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13a7.8 7.8 0 0 0 0-2l2-1.5-2-3.4-2.3 1a7.6 7.6 0 0 0-1.7-1l-.3-2.6h-3.8l-.3 2.6a7.6 7.6 0 0 0-1.7 1l-2.3-1-2 3.4L4.6 11a7.8 7.8 0 0 0 0 2l-2 1.5 2 3.4 2.3-1a7.6 7.6 0 0 0 1.7 1l.3 2.6h3.8l.3-2.6a7.6 7.6 0 0 0 1.7-1l2.3 1 2-3.4z",
  profile: "M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3",
  chevdown: "M5 8l5 5 5-5",
  chevright: "M9 5l5 5-5 5",
  arrowupright: "M7 17L17 7M8 7h9v9",
  arrowdownright: "M7 7l10 10M17 8v9H8",
  sparkles:
    "M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4zM18 14l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z",
  loader: "M21 12a9 9 0 1 1-6.2-8.5",
  star: "M12 2.5l2.9 6 6.6.6-5 4.3 1.5 6.4L12 16.9 6 19.8l1.5-6.4-5-4.3 6.6-.6z",
  alert: "M12 3l9.5 16.5h-19zM12 10v4M12 17.5h.01",
  check: "M4 12l5 5L20 6",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2",
  bulb: "M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.8.7 1 1 1 2.5h6c0-1.5.2-1.8 1-2.5A6 6 0 0 0 12 3z",
  trending: "M3 17l6-6 4 4 8-8M15 7h6v6",
  plus: "M12 5v14M5 12h14",
  filter: "M3 5h18l-7 8v6l-4-2v-4z",
  reply: "M9 17l-6-6 6-6M3 11h11a6 6 0 0 1 6 6v2",
  external: "M14 4h6v6M20 4l-9 9M18 13v6H5V6h6",
  copy: "M9 9h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1",
  flame:
    "M12 22c4 0 7-2.7 7-7 0-3-2-5-3-7-1.5 1-2 2-2.5 2C13 7 13 3 9.5 2c.5 3-2.5 4.5-4 8a7 7 0 0 0 6.5 12z",
  shield: "M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z",
  heart:
    "M12 20s-7-4.3-9.5-8.6C.8 8.3 2.4 4.5 6 4.5c2 0 3.3 1.2 4 2.3.7-1.1 2-2.3 4-2.3 3.6 0 5.2 3.8 3.5 6.9C19 15.7 12 20 12 20z",
  target:
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 12h.01",
  menu: "M4 6h16M4 12h16M4 18h16",
  arrowright: "M5 12h14M13 6l6 6-6 6",
  logout: "M10 17l5-5-5-5M15 12H3M21 5v14a2 2 0 0 1-2 2h-6",
  moon: "M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1",
  dots: "M5 12h.01M12 12h.01M19 12h.01",
  inbox: "M3 13h5l2 3h4l2-3h5M5 5h14l2 8v6H3v-6z",
  globe:
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z",
};

const props = defineProps({
  name: { type: String, required: true },
  size: { type: Number, default: 18 },
  sw: { type: Number, default: 1.8 },
  fill: { type: Boolean, default: false },
});

const d = computed(() => ICONS[props.name] || "");
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    :fill="fill ? 'currentColor' : 'none'"
    :stroke="fill ? 'none' : 'currentColor'"
    :stroke-width="sw"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path :d="d" />
  </svg>
</template>
